import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import RootLayout from '@/layouts/RootLayout';
import Home from '@/pages/Home';
import { authEnabled, googleAuthEnabled } from '@/lib/auth';

const Learn = lazy(() => import('@/pages/Learn'));
const Section = lazy(() => import('@/pages/Section'));
const Chapter = lazy(() => import('@/pages/Chapter'));
const Techniques = lazy(() => import('@/pages/Techniques'));
const TechniquePage = lazy(() => import('@/pages/TechniquePage'));
const Tools = lazy(() => import('@/pages/Tools'));
const ToolPage = lazy(() => import('@/pages/ToolPage'));
const Papers = lazy(() => import('@/pages/Papers'));
const PapersTheme = lazy(() => import('@/pages/PapersTheme'));
const Cookbook = lazy(() => import('@/pages/Cookbook'));
const Recipe = lazy(() => import('@/pages/Recipe'));
const TaskTypes = lazy(() => import('@/pages/TaskTypes'));
const TaskTypePage = lazy(() => import('@/pages/TaskTypePage'));
const Start = lazy(() => import('@/pages/Start'));
const StartTrack = lazy(() => import('@/pages/StartTrack'));
const Glossary = lazy(() => import('@/pages/Glossary'));
const Downloads = lazy(() => import('@/pages/Downloads'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Contributing = lazy(() => import('@/pages/Contributing'));
const HowToCite = lazy(() => import('@/pages/HowToCite'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Styleguide = lazy(() => import('@/pages/Styleguide'));
const SignIn = lazy(() => import('@/pages/SignIn'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Account = lazy(() => import('@/pages/Account'));

function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      className="container-prose py-14"
    >
      <div className="skeleton h-3 w-40 rounded-sm" />
      <div className="skeleton mt-7 h-9 w-9/12 max-w-2xl rounded-sm" />
      <div className="skeleton mt-4 h-5 w-7/12 max-w-xl rounded-sm" />
      <div className="mt-12 max-w-prose space-y-3.5">
        {['w-full', 'w-11/12', 'w-10/12', 'w-full', 'w-9/12', 'w-11/12'].map(
          (w, i) => (
            <div
              key={i}
              className={`skeleton h-4 rounded-sm ${w}`}
              style={{ animationDelay: `${(i % 5) * 110}ms` }}
            />
          ),
        )}
      </div>
      <span className="sr-only">Loading page</span>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<Home />} />
        <Route
          path="learn"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Learn />
            </Suspense>
          }
        />
        <Route
          path="learn/:section"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Section />
            </Suspense>
          }
        />
        <Route
          path="learn/:section/:page"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Chapter />
            </Suspense>
          }
        />
        <Route
          path="techniques"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Techniques />
            </Suspense>
          }
        />
        <Route
          path="techniques/:slug"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TechniquePage />
            </Suspense>
          }
        />
        <Route
          path="tools"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Tools />
            </Suspense>
          }
        />
        <Route
          path="tools/:slug"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ToolPage />
            </Suspense>
          }
        />
        <Route
          path="papers"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Papers />
            </Suspense>
          }
        />
        <Route
          path="papers/:theme"
          element={
            <Suspense fallback={<RouteFallback />}>
              <PapersTheme />
            </Suspense>
          }
        />
        <Route
          path="cookbook"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Cookbook />
            </Suspense>
          }
        />
        <Route
          path="cookbook/:slug"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Recipe />
            </Suspense>
          }
        />
        <Route
          path="task-types"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TaskTypes />
            </Suspense>
          }
        />
        <Route
          path="task-types/:slug"
          element={
            <Suspense fallback={<RouteFallback />}>
              <TaskTypePage />
            </Suspense>
          }
        />
        <Route
          path="start"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Start />
            </Suspense>
          }
        />
        <Route
          path="start/:persona"
          element={
            <Suspense fallback={<RouteFallback />}>
              <StartTrack />
            </Suspense>
          }
        />
        <Route
          path="glossary"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Glossary />
            </Suspense>
          }
        />
        <Route
          path="downloads"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Downloads />
            </Suspense>
          }
        />
        <Route
          path="about"
          element={
            <Suspense fallback={<RouteFallback />}>
              <About />
            </Suspense>
          }
        />
        <Route
          path="contact"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Contact />
            </Suspense>
          }
        />
        <Route
          path="contributing"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Contributing />
            </Suspense>
          }
        />
        <Route
          path="how-to-cite"
          element={
            <Suspense fallback={<RouteFallback />}>
              <HowToCite />
            </Suspense>
          }
        />
        <Route
          path="privacy"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Privacy />
            </Suspense>
          }
        />
        <Route
          path="signin"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SignIn />
            </Suspense>
          }
        />
        <Route
          path="signup"
          element={
            <Suspense fallback={<RouteFallback />}>
              <SignUp />
            </Suspense>
          }
        />
        {/* Google OAuth redirect target. With VITE_AUTH_GOOGLE=1 it runs
            the code exchange; otherwise (email-only or dark builds, plus
            stale bookmarks) it lands somewhere sensible, not a 404. */}
        <Route
          path="auth/callback"
          element={
            googleAuthEnabled ? (
              <Suspense fallback={<RouteFallback />}>
                <AuthCallback />
              </Suspense>
            ) : (
              <Navigate to={authEnabled ? '/signin' : '/'} replace />
            )
          }
        />
        <Route
          path="account"
          element={
            <Suspense fallback={<RouteFallback />}>
              <Account />
            </Suspense>
          }
        />
        {import.meta.env.DEV ? (
          <Route
            path="styleguide"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Styleguide />
              </Suspense>
            }
          />
        ) : null}
        <Route
          path="*"
          element={
            <Suspense fallback={<RouteFallback />}>
              <NotFound />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
